import {
    Button,
    Card,
    CardActions,
    CardContent,
    colors,
    Grid,
    Modal,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {useEffect, useState} from 'react';
import {T} from 'react-polyglot-hooks';
import {getHandleFieldChange, getHasError} from '../../utils/formHelpers';
import validate from 'validate.js';
import contactUs, {ContactUsVars} from './contactUs.api';
import {useSnackbar} from 'notistack';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: 700,
        maxHeight: '100%',
        overflowY: 'auto',
        maxWidth: '100%'
    },
    container: {
        marginTop: theme.spacing(3)
    },
    actions: {
        justifyContent: 'flex-end'
    },
    deleteButton: {
        color: theme.palette.white,
        backgroundColor: colors.red[600],
        '&:hover': {
            backgroundColor: colors.red[900]
        }
    }
}));

export const ContactUsContent = (props: {
    className?: string,
    onClose?: () => void,
    onSendClick?: () => void,
    heading: string,
    message: string,
    groupId?: number,
    adminMessage?: string,
    email?: string,
    name?: string
}) => {
    const {onClose, onSendClick, heading, message, className, adminMessage, groupId, email, name,...rest} = props;
    const classes = useStyles();
    const [sendContactUs, contactUsData] = contactUs();
    const {enqueueSnackbar} = useSnackbar();

    //1. Set form and error state
    const [formState, setFormState] = useState({
        name: name ? name : '',
        email: email ? email : '',
        message: '',
    });

    //tours
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            name: false,
            email: false,
            message: false,
        }
    });

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        name: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        email: {
            presence: {allowEmpty: false, message: 'is required'},
            email: true,
            length: {
                maximum: 64
            }
        },
        message: {
            presence: {allowEmpty: false, message: 'is required'},
        },
    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);

        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);

    if (!open) {
        return null;
    }


    const handleSubmit = () => {
        //sanitise / translate to graphql objects
        const sanitisedData: ContactUsVars = {
            data: {
                group_id: groupId,
                email: formState.email,
                name: formState.name,
                message: formState.message,
                adminMessage,
            }
        };
        //send to server.
        sendContactUs({variables: sanitisedData})
            .then(() => {
                enqueueSnackbar('Contact Us Submitted', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Contact Us Failed!', {variant: 'error'});
            });
    };

    return (
        <Card >
            <form>
                <CardContent>
                    <Typography gutterBottom variant="h3">{heading}</Typography>
                    <Typography gutterBottom variant="body1">{message}</Typography>
                    <Grid container spacing={3}>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                {...hasError('name')}
                                onChange={handleFieldChange}
                                value={formState.name}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                {...hasError('email')}
                                onChange={handleFieldChange}
                                value={formState.email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                rowsMax={3}
                                label="Message"
                                name="message"
                                {...hasError('message')}
                                onChange={handleFieldChange}
                                value={formState.message}
                            />
                        </Grid>
                    </Grid>

                </CardContent>
                <CardActions className={classes.actions}>
                    {onClose && (
                        <Button onClick={onClose} variant="contained">
                            <T phrase="Cancel"/>
                        </Button>
                    )}
                    <Button className={classes.deleteButton} disabled={!errors.isValid} onClick={() => {
                        if(onClose) onClose();
                        handleSubmit();
                        if (onSendClick) onSendClick();
                    }} variant="contained">
                        <T phrase="Send"/>
                    </Button>
                </CardActions>
            </form>
        </Card>
    );
};

const ContactUs = (props: {
    className?: string,
    onClose: () => void,
    onSendClick?: () => void,
    open: boolean,
    heading: string,
    message: string,
    groupId: number,
    adminMessage?: string,
}) => {
    const {open, onClose, className, ...rest} = props;
    const classes = useStyles();

    return (
        <Modal onClose={onClose} open={open}>
            <div {...rest} className={clsx(classes.root, className)}>
                <ContactUsContent onClose={onClose} {...rest}/>
            </div>
        </Modal>
    );
};

export default ContactUs;
